/// <reference types="geojson" />
import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, NgZone,
  OnChanges, OnDestroy, Output, PLATFORM_ID, SimpleChanges, ViewChild
} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {FilterbaseService} from '../../pages/shop/top-filter/filter-base/services/filterbase.service';
import {AppService} from '../../app.service';
import {NadlanMapService} from './nadlan-map.service';

/**
 * SCRUM-253 – real-estate results on a free map.
 *
 * Map: OpenFreeMap tiles (OpenStreetMap data) drawn by MapLibre GL – no API
 * key, no billing. MapLibre draws Hebrew / Arabic labels natively.
 * Points come from `clientAjax/nadlanMapPoints` (every ad matching the
 * current filters); positions are street-level only (ads have no house
 * number). Attribution (© OpenMapTiles © OpenStreetMap contributors) is
 * shown by MapLibre and must never be hidden.
 */

const STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';
const ISRAEL_CENTER: [number, number] = [34.95, 31.75];
const BRAND_BLUE = '#2563eb';
const VIP_ORANGE = '#f97316';
const MAX_LOGO_MARKERS = 120;

export interface MapPoint {
  id: number; lat: number; lng: number; precision: 'street' | 'city';
  price: number | null;
  // SCRUM-263: paid per-ad routes are 'promoted' (the server called them 'vip' before).
  type: 'private' | 'business' | 'promoted' | 'vip';
  logo?: string; storeName?: string;
}

@Component({
  selector: 'app-nadlan-map',
  templateUrl: './nadlan-map.component.html',
  styleUrls: ['./nadlan-map.component.css'],
  standalone: false
})
export class NadlanMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** Ads currently loaded in the list (used to fit the map and for previews). */
  @Input() products: any[] = [];
  /** Changes whenever the filters are applied – reloads the points. */
  @Input() reloadKey = 0;
  /** A "חיפוש באזור זה" area is active on the list. */
  @Input() areaActive = false;
  /** Show the close (×) button – desktop hides the map, mobile returns to the list. */
  @Input() closeLabel = 'הסתר מפה';
  @Output() searchArea = new EventEmitter<string>();
  @Output() clearArea = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('mapEl', {static: true}) mapEl!: ElementRef<HTMLDivElement>;

  loading = true;
  failed = false;
  movedByUser = false;
  totalPoints = 0;
  truncated = false;
  /** Places the server is still looking up (ads published before the map). */
  pending = 0;
  private pendingTimer: any;
  private pendingPolls = 0;
  selected: { id: number; product: any | null; loading: boolean } | null = null;

  private ml: any = null;            // the maplibre-gl module (loaded on demand)
  private map: any = null;
  private ready = false;
  private points = new Map<number, MapPoint>();
  private fanned = new Map<number, [number, number]>();
  private logoMarkers = new Map<number, any>();
  private subs = new Subscription();
  private resizeObs?: ResizeObserver;
  private loadTimer: any;
  private fittedOnce = false;
  private fittedToList = false;
  private restoreSelectedId: number | null = null;
  /** Captured on creation - router.url already points at the next page by
   *  the time ngOnDestroy runs. */
  private pageUrl = '';
  private pointsReq?: Subscription;

  constructor(private filterbase: FilterbaseService,
              private appService: AppService,
              private mapService: NadlanMapService,
              private zone: NgZone,
              private cdr: ChangeDetectorRef,
              private router: Router,
              @Inject(PLATFORM_ID) private platformId: object) {}

  // ── lifecycle ────────────────────────────────────────────────

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.pageUrl = this.router.url.split('#')[0];
    this.mapService.active = true;
    this.zone.runOutsideAngular(() => this.createMap());
    this.subs.add(this.mapService.hover$.subscribe(h => this.showFocus(h.id)));
    this.subs.add(this.mapService.focus$.subscribe(id => this.zone.run(() => this.focusAd(id))));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reloadKey'] && !changes['reloadKey'].firstChange && this.ready) {
      this.fittedOnce = this.fittedToList = this.areaActive;   // keep the camera during an area search
      this.pendingPolls = 0;
      this.loadPoints();
    }
    if (changes['products'] && this.ready && !this.fittedToList && !this.areaActive
        && (this.products?.length ?? 0) > 0 && this.points.size > 0) {
      this.fitToResults();
    }
  }

  ngOnDestroy(): void {
    this.mapService.active = false;
    this.mapService.hover(null, 'map');
    if (this.map && this.ready) {
      const c = this.map.getCenter();
      this.mapService.lastCamera = {
        center: [c.lng, c.lat],
        zoom: this.map.getZoom(),
        url: this.pageUrl,
        selectedId: this.selected?.id ?? null,
        mobileMapOpen: this.closeLabel === 'לרשימה',
      };
    }
    clearTimeout(this.loadTimer);
    clearTimeout(this.pendingTimer);
    this.pointsReq?.unsubscribe();
    this.subs.unsubscribe();
    this.resizeObs?.disconnect();
    this.logoMarkers.forEach(m => m.remove());
    try { this.map?.remove(); } catch { /* already gone */ }
  }

  // ── map setup ────────────────────────────────────────────────

  private async createMap(): Promise<void> {
    try {
      const mod: any = await import('maplibre-gl');
      this.ml = mod.default ?? mod;
      // The worker files are copied to /assets/maplibre by angular.json.
      this.ml.setWorkerUrl(new URL('assets/maplibre/maplibre-gl-worker.mjs', document.baseURI).href);
    } catch (e) {
      console.error('map library failed to load', e);
      return this.zone.run(() => this.fail());
    }
    // Coming back to the same results page (e.g. "back" from an ad): open
    // exactly where the user was and don't re-fit to the results.
    const cam = this.mapService.cameraFor(this.pageUrl);
    if (cam) {
      this.fittedOnce = true;
      this.fittedToList = true;
      this.restoreSelectedId = cam.selectedId;
    }
    this.map = new this.ml.Map({
      container: this.mapEl.nativeElement,
      style: STYLE_URL,
      center: cam?.center ?? ISRAEL_CENTER,
      zoom: cam?.zoom ?? 7,
      minZoom: 6,
      maxZoom: 18,
      maxBounds: [[33.3, 29.2], [36.6, 33.6]],
      attributionControl: {compact: true},
      dragRotate: false,
      pitchWithRotate: false,
      cooperativeGestures: false,
    });
    this.map.touchZoomRotate.disableRotation();
    this.map.addControl(new this.ml.NavigationControl({showCompass: false}), 'top-left');
    this.map.addControl(new this.ml.GeolocateControl({
      positionOptions: {enableHighAccuracy: false, timeout: 8000},
      trackUserLocation: false,
      showAccuracyCircle: false,
    }), 'top-left');

    this.loadTimer = setTimeout(() => { if (!this.ready) this.zone.run(() => this.fail()); }, 20000);
    this.map.on('error', (e: any) => {
      // Tile hiccups after the map is up are harmless; a style failure is not.
      if (!this.ready) { console.warn('map error', e?.error || e); this.zone.run(() => this.fail()); }
    });
    this.map.on('load', () => this.onLoad());

    this.resizeObs = new ResizeObserver(() => this.map?.resize());
    this.resizeObs.observe(this.mapEl.nativeElement);
  }

  private onLoad(): void {
    clearTimeout(this.loadTimer);
    this.ready = true;
    this.localizeLabels();

    this.map.addSource('ads', {
      type: 'geojson',
      data: {type: 'FeatureCollection', features: []},
      cluster: true,
      clusterRadius: 46,
      clusterMaxZoom: 15,
    });
    this.map.addSource('focus', {type: 'geojson', data: {type: 'FeatureCollection', features: []}});

    this.map.addLayer({
      id: 'clusters', type: 'circle', source: 'ads', filter: ['has', 'point_count'],
      paint: {
        'circle-color': BRAND_BLUE,
        'circle-opacity': 0.92,
        'circle-radius': ['step', ['get', 'point_count'], 15, 10, 18, 50, 22, 200, 27, 1000, 32],
        'circle-stroke-width': 3,
        'circle-stroke-color': 'rgba(255,255,255,0.95)',
      },
    });
    this.map.addLayer({
      id: 'cluster-count', type: 'symbol', source: 'ads', filter: ['has', 'point_count'],
      layout: {
        'text-field': ['get', 'point_count_abbreviated'],
        'text-font': ['Noto Sans Bold'],
        'text-size': 12.5,
        'text-allow-overlap': true,
      },
      paint: {'text-color': '#ffffff'},
    });
    this.map.addLayer({
      id: 'ad-dots', type: 'circle', source: 'ads',
      filter: ['all', ['!', ['has', 'point_count']], ['!=', ['get', 'type'], 'business']],
      paint: {
        'circle-color': ['match', ['get', 'type'], ['promoted', 'vip'], VIP_ORANGE, BRAND_BLUE],
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 5, 14, 7, 17, 8.5],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    });
    this.map.addLayer({
      id: 'focus-ring', type: 'circle', source: 'focus',
      paint: {
        'circle-radius': 15,
        'circle-color': 'rgba(37, 99, 235, 0.18)',
        'circle-stroke-width': 2.5,
        'circle-stroke-color': '#1d4ed8',
      },
    });

    // Clusters: zoom in.
    this.map.on('click', 'clusters', async (e: any) => {
      const f = e.features?.[0];
      if (!f) return;
      try {
        const zoom = await this.map.getSource('ads').getClusterExpansionZoom(f.properties.cluster_id);
        this.map.easeTo({center: f.geometry.coordinates, zoom: Math.min(zoom + 0.3, 17)});
      } catch { this.map.easeTo({center: f.geometry.coordinates, zoom: this.map.getZoom() + 2}); }
    });
    // Single ads: preview card + hover sync.
    this.map.on('click', 'ad-dots', (e: any) => {
      const id = Number(e.features?.[0]?.properties?.id);
      if (id) this.zone.run(() => this.select(id));
    });
    this.map.on('mousemove', 'ad-dots', (e: any) => {
      this.map.getCanvas().style.cursor = 'pointer';
      const id = Number(e.features?.[0]?.properties?.id) || null;
      if (id !== this.mapService.hoveredId) this.zone.run(() => this.mapService.hover(id, 'map'));
    });
    this.map.on('mouseleave', 'ad-dots', () => {
      this.map.getCanvas().style.cursor = '';
      this.zone.run(() => this.mapService.hover(null, 'map'));
    });
    this.map.on('mouseenter', 'clusters', () => this.map.getCanvas().style.cursor = 'pointer');
    this.map.on('mouseleave', 'clusters', () => this.map.getCanvas().style.cursor = '');
    // Empty map click closes the preview.
    this.map.on('click', (e: any) => {
      const hit = this.map.queryRenderedFeatures(e.point, {layers: ['ad-dots', 'clusters']});
      if (!hit.length && this.selected) this.zone.run(() => this.closePreview());
    });

    // "חיפוש באזור זה" appears only after the USER moves the map.
    this.map.on('moveend', (e: any) => {
      if (e.originalEvent && !this.movedByUser) this.zone.run(() => this.movedByUser = true);
      this.syncLogoMarkers();
    });
    this.map.on('sourcedata', (e: any) => {
      if (e.sourceId === 'ads' && e.isSourceLoaded) this.syncLogoMarkers();
    });

    this.zone.run(() => this.loadPoints());
  }

  /** Hebrew names first (OpenStreetMap `name:he`), local name otherwise. */
  private localizeLabels(): void {
    const field = ['coalesce', ['get', 'name:he'], ['get', 'name']];
    for (const layer of this.map.getStyle().layers || []) {
      const tf = layer.layout?.['text-field'];
      if (!tf || layer.type !== 'symbol') continue;
      const raw = JSON.stringify(tf);
      if (raw.includes('name')) {
        try { this.map.setLayoutProperty(layer.id, 'text-field', field); } catch { /* skip */ }
      }
    }
  }

  // ── data ─────────────────────────────────────────────────────

  private loadPoints(): void {
    this.loading = true;
    this.pointsReq?.unsubscribe();
    this.pointsReq = this.filterbase.loadMapPoints().subscribe({
      next: (res: any) => {
        const list: MapPoint[] = res?.points || [];
        this.totalPoints = res?.total ?? list.length;
        this.truncated = !!res?.truncated;
        this.pending = res?.pending ?? 0;
        // Positions are being looked up on the server – refresh until done.
        clearTimeout(this.pendingTimer);
        if (this.pending > 0 && this.pendingPolls < 30) {
          this.pendingPolls++;
          this.pendingTimer = setTimeout(() => this.loadPoints(), 10000);
        }
        this.points.clear();
        list.forEach(p => this.points.set(p.id, p));
        this.fanned = this.fanOut(list);
        const features = list.map(p => ({
          type: 'Feature',
          geometry: {type: 'Point', coordinates: this.fanned.get(p.id)},
          properties: {id: p.id, type: p.type},
        }));
        this.map?.getSource('ads')?.setData({type: 'FeatureCollection', features});
        this.loading = false;
        if (!this.fittedOnce) this.fitToResults();
        this.showFocus(this.mapService.hoveredId);
        if (this.restoreSelectedId != null && this.points.has(this.restoreSelectedId)) {
          const id = this.restoreSelectedId;
          this.restoreSelectedId = null;
          this.select(id);
        }
        this.cdr.markForCheck();
      },
      error: () => { this.loading = false; this.cdr.markForCheck(); },
    });
  }

  /** Ads on the same street share one position – spread them in a small
   *  spiral so each stays clickable (clusters cover the zoomed-out view). */
  private fanOut(list: MapPoint[]): Map<number, [number, number]> {
    const groups = new Map<string, MapPoint[]>();
    for (const p of list) {
      const key = `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
      (groups.get(key) ?? groups.set(key, []).get(key)!).push(p);
    }
    const out = new Map<number, [number, number]>();
    groups.forEach(group => {
      group.forEach((p, i) => {
        if (group.length === 1 || i === 0) { out.set(p.id, [p.lng, p.lat]); return; }
        const r = 0.00013 * Math.sqrt(i);              // ~15 m steps
        const a = i * 2.39996;                          // golden angle
        out.set(p.id, [p.lng + r * Math.cos(a) / Math.cos(p.lat * Math.PI / 180), p.lat + r * Math.sin(a)]);
      });
    });
    return out;
  }

  /** Fit to the ads loaded in the list (fallback: every point). */
  private fitToResults(): void {
    if (!this.map || !this.ready || !this.ml) return;
    const coords: [number, number][] = [];
    for (const item of this.products || []) {
      const c = this.fanned.get(item?.id) ?? this.coordsOf(item);
      if (c) coords.push(c);
    }
    if (coords.length) this.fittedToList = true;
    else this.fanned.forEach(c => coords.push(c));
    if (!coords.length) return;
    this.fittedOnce = true;
    if (coords.length === 1) {
      this.map.jumpTo({center: coords[0], zoom: 14});
      return;
    }
    const b = new this.ml.LngLatBounds(coords[0], coords[0]);
    coords.forEach(c => b.extend(c));
    this.map.fitBounds(b, {padding: 48, maxZoom: 14, duration: 0});
  }

  private coordsOf(item: any): [number, number] | null {
    const s = item?.Street, c = item?.City;
    if (s?.lat != null && s?.lng != null) return [s.lng, s.lat];
    if (c?.lat != null && c?.lng != null) return [c.lng, c.lat];
    return null;
  }

  // ── business logo markers ───────────────────────────────────

  private syncLogoMarkers(): void {
    if (!this.map || !this.ready) return;
    let feats: any[] = [];
    try {
      feats = this.map.querySourceFeatures('ads', {
        filter: ['all', ['!', ['has', 'point_count']], ['==', ['get', 'type'], 'business']],
      });
    } catch { return; }
    const bounds = this.map.getBounds();
    const wanted = new Map<number, [number, number]>();
    for (const f of feats) {
      const id = Number(f.properties?.id);
      const c = this.fanned.get(id);
      if (!id || !c || wanted.has(id) || !bounds.contains(c)) continue;
      wanted.set(id, c);
      if (wanted.size >= MAX_LOGO_MARKERS) break;
    }
    this.logoMarkers.forEach((m, id) => {
      if (!wanted.has(id)) { m.remove(); this.logoMarkers.delete(id); }
    });
    wanted.forEach((c, id) => {
      if (this.logoMarkers.has(id)) return;
      const p = this.points.get(id);
      if (!p) return;
      const el = this.logoElement(p);
      const marker = new this.ml.Marker({element: el, anchor: 'bottom'}).setLngLat(c).addTo(this.map);
      this.logoMarkers.set(id, marker);
    });
    this.markHoveredLogo(this.mapService.hoveredId);
  }

  private logoElement(p: MapPoint): HTMLElement {
    const el = document.createElement('button');
    el.type = 'button';
    el.className = 's4a-logo-marker';
    // SCRUM-262/263: a business without a profile still gets a marker,
    // labelled with the real-estate tag.
    el.setAttribute('aria-label', p.storeName || 'מתווך');
    if (p.logo) {
      const img = document.createElement('img');
      img.src = p.logo;
      img.alt = '';
      img.loading = 'lazy';
      img.onerror = () => { img.remove(); el.textContent = p.storeName || 'מתווך'; };
      el.appendChild(img);
    } else {
      el.textContent = p.storeName || 'מתווך';
    }
    el.addEventListener('click', ev => { ev.stopPropagation(); this.zone.run(() => this.select(p.id)); });
    el.addEventListener('mouseenter', () => this.zone.run(() => this.mapService.hover(p.id, 'map')));
    el.addEventListener('mouseleave', () => this.zone.run(() => this.mapService.hover(null, 'map')));
    return el;
  }

  private markHoveredLogo(id: number | null): void {
    this.logoMarkers.forEach((m, mid) => m.getElement().classList.toggle('is-hover', mid === id));
  }

  // ── list ↔ map ───────────────────────────────────────────────

  private showFocus(id: number | null): void {
    if (!this.map || !this.ready) return;
    const c = id != null ? this.fanned.get(id) : undefined;
    const data = c
      ? {type: 'FeatureCollection', features: [{type: 'Feature', geometry: {type: 'Point', coordinates: c}, properties: {}}]}
      : {type: 'FeatureCollection', features: []};
    this.map.getSource('focus')?.setData(data);
    this.markHoveredLogo(id);
  }

  private focusAd(id: number): void {
    const c = this.fanned.get(id) ?? this.coordsOf(this.products.find(p => p?.id === id));
    if (!c || !this.map) return;
    this.map.flyTo({center: c, zoom: Math.max(this.map.getZoom(), 15), speed: 1.4});
    this.select(id);
  }

  // ── preview card ─────────────────────────────────────────────

  select(id: number): void {
    const fromList = (this.products || []).find(p => p?.id === id) || null;
    this.selected = {id, product: fromList, loading: !fromList};
    this.mapService.hover(id, 'map');
    if (!fromList) {
      this.appService.getProductById(id).subscribe({
        next: product => { if (this.selected?.id === id) this.selected = {id, product, loading: false}; },
        error: () => { if (this.selected?.id === id) this.selected = {id, product: null, loading: false}; },
      });
    }
  }

  closePreview(): void {
    this.selected = null;
    this.mapService.hover(null, 'map');
  }

  saveSelected(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.selected?.product) this.appService.addToWishList(this.selected.product);
  }

  previewImage(p: any): string { return p?.images?.[0]?.image || ''; }

  previewPrice(p: any): string {
    const price = p?.price ?? this.points.get(p?.id)?.price;
    if (price == null || price === 0) return 'מחיר לא צוין';
    const text = '₪' + Number(price).toLocaleString('he-IL');
    return p?.sconderyCategory?.subCategory === 'RENTAL' ? `${text} / חודש` : text;
  }

  previewAddress(p: any): string {
    return [p?.Street?.nameHe, p?.City?.nameHe].filter(Boolean).join(', ');
  }

  // ── "חיפוש באזור זה" ─────────────────────────────────────────

  searchThisArea(): void {
    if (!this.map) return;
    const b = this.map.getBounds();
    const bbox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()].map(v => v.toFixed(5)).join(',');
    this.movedByUser = false;
    this.searchArea.emit(bbox);
  }

  onClearArea(): void {
    this.movedByUser = false;
    this.clearArea.emit();
  }

  private fail(): void {
    this.failed = true;
    this.loading = false;
    this.cdr.markForCheck();
  }
}
